import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GuideCard = ({ guide, index }) => {
  const { language } = useLanguage();
  const title = language === 'it' ? guide.title_it : guide.title_en;
  const description = language === 'it' ? guide.description_it : guide.description_en;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      data-testid={`guide-card-${guide.id}`}
    >
      <Link
        to={`/preview/${guide.id}`}
        className="group block relative overflow-hidden rounded-2xl bg-[#1a1a1a] hover:bg-[#222] transition-all duration-500"
      >
        {/* Image Container */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={guide.image_url}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading={index < 2 ? "eager" : "lazy"}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-transparent to-transparent opacity-80" />
          
          {/* Location Badge */}
          <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full">
            <MapPin className="w-3.5 h-3.5 text-[#C9B896]" />
            <span className="text-xs font-medium text-white">{guide.category}</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#C9B896] transition-colors line-clamp-2">
            {title}
          </h3>
          <p className="text-[#888] text-sm mb-4 leading-relaxed line-clamp-2">
            {description}
          </p>
          
          {/* Price and CTA */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {guide.original_price && guide.original_price > guide.price && (
                <span className="text-sm text-[#666] line-through">
                  €{guide.original_price.toFixed(2).replace('.', ',')}
                </span>
              )}
              <span className="text-xl font-bold text-white">
                €{guide.price.toFixed(2).replace('.', ',')}
              </span>
            </div>
            <span className="flex items-center gap-1 text-[#C9B896] text-sm font-medium group-hover:gap-2 transition-all">
              {language === 'it' ? 'Scopri' : 'Explore'}
              <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const GuidesSection = ({ showAll = false }) => {
  const { language } = useLanguage();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  const texts = {
    it: {
      title: "Le Nostre Guide",
      subtitle: "Quattro destinazioni iconiche, curate nei minimi dettagli",
      viewAll: "Vedi tutte le guide"
    },
    en: {
      title: "Our Guides",
      subtitle: "Four iconic destinations, curated to the smallest detail",
      viewAll: "View all guides"
    }
  };

  const t = texts[language];

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        const response = await axios.get(`${API}/guides`);
        setGuides(response.data);
      } catch (error) {
        console.error('Error fetching guides:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGuides();
  }, []);

  if (loading) {
    return (
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="aspect-[4/3] bg-[#1a1a1a] rounded-2xl animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-[#0A0A0A]" data-testid="guides-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          {/* Launch Badge */}
          <div className="inline-flex items-center gap-2 bg-[#C9B896]/10 border border-[#C9B896]/20 px-4 py-2 rounded-full mb-6">
            <span className="w-2 h-2 bg-[#C9B896] rounded-full animate-pulse"></span>
            <span className="text-sm font-medium text-[#C9B896]">
              {language === 'it' ? 'Offerta Lancio' : 'Launch Offer'}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-[#888] max-w-xl mx-auto">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Grid - 4 equal cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {guides.map((guide, index) => (
            <GuideCard key={guide.id} guide={guide} index={index} />
          ))}
        </div>

        {/* View All */}
        {!showAll && guides.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <Link
              to="/guides"
              className="inline-flex items-center gap-2 text-[#C9B896] hover:text-white font-medium transition-colors group"
              data-testid="view-all-guides"
            >
              {t.viewAll}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default GuidesSection;
